/**
 * Backend follow-up suggestions.
 *
 * After a turn's `completion` the backend may send
 *
 *   { "type": "follow_ups",
 *     "data": { "items": ["for a party", "casual styles", "show cheaper ones"] },
 *     "timestamp": "2026-10-22T08:11:35.279935+00:00" }
 *
 * — short things the shopper could say next. It lands about half a second
 * after the turn's promise has resolved, so no per-turn listener can see it;
 * this is a socket-level register (toastManager's shape) that every shared
 * socket is bound to by orbieeChatTurn, like the welcome and the takeover.
 *
 * Who shows them is decided here, once: the chat panel, and only the chat
 * panel (`subscribeChatFollowUps`). A voice or avatar reply is spoken, so
 * chips under a thread the shopper is not reading would answer a question
 * they never saw — the mode register (services/assistantMode.js) says whose
 * turn it was.
 */
import { ServerMessageType } from "./orbieeChatSocket";
import { getAssistantMode } from "../assistantMode";

/** More than this reads as a menu, not a nudge. */
export const MAX_FOLLOW_UPS = 6;

/** @returns {boolean} true for the backend's follow-ups frame */
export function isFollowUpsFrame(frame) {
  return (
    !!frame &&
    typeof frame === "object" &&
    frame.type === ServerMessageType.FOLLOW_UPS
  );
}

/**
 * The frame's items as clean, distinct strings — trimmed, empties and
 * non-strings dropped, case-insensitive duplicates folded, capped — or `[]`
 * for anything malformed. The backend's own order is kept.
 */
export function followUpItems(frame) {
  const raw = frame?.data?.items;
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const value of raw) {
    if (typeof value !== "string") continue;
    const text = value.trim();
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
    if (out.length >= MAX_FOLLOW_UPS) break;
  }
  return out;
}

/** @type {Set<(items: string[], frame: object) => void>} */
const listeners = new Set();

/**
 * Every follow-ups frame, whoever's turn it was: `cb(items, frame)`.
 * Returns the unsubscribe.
 */
export function subscribeOrbieeFollowUps(cb) {
  if (typeof cb !== "function") return () => {};
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Follow-ups for the chat panel only: delivered when the conversation is
 * the typed chat's (`getAssistantMode() === "chat"`), dropped for a voice
 * or avatar turn. Returns the unsubscribe.
 */
export function subscribeChatFollowUps(cb) {
  return subscribeOrbieeFollowUps((items, frame) => {
    if (getAssistantMode() !== "chat") return;
    cb(items, frame);
  });
}

/**
 * Route a socket's follow-ups frames to the subscribers. Bound by
 * orbieeChatTurn to every shared socket; returns the unwatch function.
 */
export function watchSocketForFollowUps(socket) {
  return socket.on("follow-ups", (frame) => {
    const items = followUpItems(frame);
    [...listeners].forEach((cb) => {
      try {
        cb(items, frame);
      } catch (err) {
        console.error("[FollowUps] listener threw", err);
      }
    });
  });
}
